import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, FAQS, SITE } from "@/mocks/gossip";

const SUBMIT = "https://readdy.ai/api/form/daemaqhtto0ceqqngvvg";

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "err">("idle");
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get("company_alt") as string) || "";
    if (honeypot.trim()) {
      setStatus("ok");
      form.reset();
      setTimeout(() => setStatus("idle"), 3000);
      return;
    }
    const email = (fd.get("email") as string) || "";
    if (!email) return;
    setStatus("sending");
    try {
      const res = await fetch(SUBMIT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email }).toString(),
      });
      const text = await res.text();
      const parsed = text ? JSON.parse(text) : {};
      const serverMsg = parsed?.meta?.message || parsed?.message || "";
      const ok =
        res.ok && parsed?.code === "OK" && !/spam/i.test(serverMsg);
      if (ok) {
        setStatus("ok");
        form.reset();
      } else {
        setStatus("err");
      }
    } catch {
      setStatus("err");
    } finally {
      setTimeout(() => setStatus("idle"), 3500);
    }
  };

  return (
    <footer className="bg-accent-100 border-t border-accent-200 text-foreground-800">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* 品牌 */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="w-10 h-10 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50">
                <i className="ri-fire-line text-xl"></i>
              </span>
              <span className="font-heading text-2xl text-foreground-950">九一吃瓜</span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              {SITE.desc}
            </p>
            <div className="mt-5 flex items-center gap-2">
              <a href="#!" rel="nofollow" aria-label="微信" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#!" rel="nofollow" aria-label="微博" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#!" rel="nofollow" aria-label="话题" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:bg-secondary-500 hover:text-background-50 transition">
                <i className="ri-hashtag"></i>
              </a>
            </div>
          </div>

          {/* 快速栏目 */}
          <div>
            <h3 className="text-sm font-bold text-foreground-950 tracking-wider mb-4">热门栏目</h3>
            <ul className="space-y-2.5 text-sm">
              {CATEGORIES.map((c) => (
                <li key={c.id}>
                  <a href="#feed" onClick={(e) => e.preventDefault()} className="text-foreground-600 hover:text-primary-600 transition">
                    {c.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* 快速链接 */}
          <div>
            <h3 className="text-sm font-bold text-foreground-950 tracking-wider mb-4">围观指南</h3>
            <ul className="space-y-2.5 text-sm">
              {FAQS.slice(0, 4).map((f, i) => (
                <li key={i}>
                  <a href="#faq" onClick={(e) => e.preventDefault()} className="text-foreground-600 hover:text-primary-600 transition">
                    {f.q.replace(/[？?]$/, "")}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* 订阅 */}
          <div>
            <h3 className="text-sm font-bold text-foreground-950 tracking-wider mb-4">订阅新瓜速递</h3>
            <p className="text-sm text-foreground-600 mb-4">第一时间收到每日热点速览，不错过任何前排现场。</p>
            <form ref={formRef} onSubmit={onSubmit} data-readdy-form className="space-y-3">
              <div className="field-safety">
                <label htmlFor="alt-company">公司名称</label>
                <input id="alt-company" type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <input
                type="email"
                name="email"
                required
                placeholder="输入你的邮箱"
                className="w-full rounded-full border border-accent-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition"
              />
              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full rounded-full bg-primary-500 text-background-50 text-sm font-medium py-2.5 hover:bg-primary-600 disabled:opacity-60 transition whitespace-nowrap"
              >
                {status === "sending"
                  ? "提交中…"
                  : status === "ok"
                  ? "已订阅，感谢关注"
                  : "订阅新瓜速递"}
              </button>
              {status === "err" && (
                <p className="text-xs text-primary-600">订阅失败，请稍后重试。</p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-accent-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-foreground-500">
          <p>© {new Date().getFullYear()} {SITE.name} · 热爱围观，理性吃瓜。</p>
          <p className="flex items-center gap-1.5">
            <i className="ri-mail-line"></i> {SITE.email}
          </p>
        </div>
      </div>
    </footer>
  );
}